/**
 * The first thing on the Backup board: **what needs you, in order.**
 *
 * Every other panel here answers a question about the estate. This one answers a
 * question about the person reading it, and those are not the same. The routine
 * this board replaced was a single loop — open Veeam, filter the last 24 hours,
 * write down the errors — and the board that replaced it spread the same
 * information across seven panels that each have to be read and interpreted
 * before they yield a task. Finding the work became work.
 *
 * Altitude (design rules §1): this is **L0 with teeth**. The triage strip is
 * three numbers, and the list underneath is a "worst N" — bounded, ranked, and
 * every row a door into the L1 or L2 panel holding its evidence. The full list
 * lives at `/backup/actions`.
 *
 * Three decisions worth stating, because each one was the difference between a
 * list people use and a list people scroll past:
 *
 * 1. **Urgency is about the clock, not severity.** *Now* is broken today.
 *    *Soon* is degrading and will be a *now*. *Backlog* is real work with no
 *    clock on it. That is the sort an operator actually does each morning, and
 *    keeping the backlog out of *now* is what stops 119 stale Arc registrations
 *    shouting louder than one failed backup.
 * 2. **A set is one row.** "119 Arc registrations stopped reporting" is one
 *    decision; 119 rows are noise that buries the failure three screens up. The
 *    count rides on the row and the drill shows the objects.
 * 3. **Every row says why.** A task list that cannot say what it costs to
 *    ignore is one people learn to skip — so `why` is required on the backend
 *    model, not optional decoration here.
 *
 * Honesty (§5): when a source has gone quiet the strip says the counts are a
 * floor, because nothing new can arrive from a source that is not reporting —
 * an empty *now* under a dead collector is the one number that must never read
 * as "all clear".
 */

import {
  AlertTriangle,
  CalendarClock,
  CircleCheck,
  ClipboardList,
  Icon,
  type IconComponent,
} from "@norican/icons";
import { Badge, EmptyState, Typography } from "@norican/react";

import type { BackupAction, BackupActions } from "@/lib/api";
import { formatCount, formatRelativeTime, type StatusTone } from "@/lib/charts/config";
import { DrillLink } from "@/lib/drill";

import { PanelCard } from "./panel-card";
import { BACKUP_ACTIONS_PATH, URGENCY_PARAM } from "./params";

/** Rows shown inline. The rest are one click away, never dropped. */
const INLINE_LIMIT = 8;

type Urgency = BackupAction["urgency"];

interface UrgencyStyle {
  label: string;
  /** What the bucket means, in the operator's words. */
  meaning: string;
  tone: StatusTone;
  icon: IconComponent;
}

/**
 * Status tones, not a categorical ramp (§3.2): these are grades of "how bad",
 * and every one is paired with an icon and a word so it is never colour-alone.
 */
const URGENCY: Record<Urgency, UrgencyStyle> = {
  now: {
    label: "Fix today",
    meaning: "A failed backup, a missed job, or a quiet source.",
    tone: "danger",
    icon: AlertTriangle,
  },
  soon: {
    label: "Degrading",
    meaning: "Not broken yet, and will be if it is left alone.",
    tone: "warning",
    icon: CalendarClock,
  },
  backlog: {
    label: "Backlog",
    meaning: "Real work, no clock on it.",
    tone: "neutral",
    icon: ClipboardList,
  },
};

const ORDER: Urgency[] = ["now", "soon", "backlog"];

/**
 * `Icon` has no `neutral` tone — the DS reserves its tones for meaning, and
 * "no particular meaning" is the absence of one rather than a colour. So the
 * backlog icon inherits the surrounding text colour, which is exactly right:
 * it is work, not a warning.
 */
function iconTone(tone: StatusTone): "success" | "warning" | "danger" | undefined {
  return tone === "neutral" ? undefined : tone;
}

export function ActionsSection({ actions }: { actions: BackupActions }) {
  if (!actions.configured) {
    return null;
  }

  const counts: Record<Urgency, number> = {
    now: actions.now,
    soon: actions.soon,
    backlog: actions.backlog,
  };
  const total = actions.now + actions.soon + actions.backlog;
  const shown = actions.actions.slice(0, INLINE_LIMIT);

  return (
    <section id="backup-actions" className="scroll-mt-6">
      <PanelCard
        title="What needs you"
        caption={
          total > shown.length ? (
            <DrillLink
              to={BACKUP_ACTIONS_PATH}
              params={{}}
              className="text-accent text-sm font-medium hover:underline"
            >
              View all {formatCount(total)} →
            </DrillLink>
          ) : (
            actions.status_detail
          )
        }
      >
        <div className="flex flex-col gap-5">
          {/* The strip: three numbers, each a door into its own bucket. A
              segmented bar would compare them, which is not the question —
              "how many of each, and is any of it urgent" is. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {ORDER.map((urgency) => (
              <UrgencyTile key={urgency} urgency={urgency} count={counts[urgency]} />
            ))}
          </div>

          {actions.stale && (
            <Typography variant="caption" tone="muted">
              A source has gone quiet, so these counts are a floor rather than a total — nothing new
              can arrive from a source that is not reporting.
            </Typography>
          )}

          {shown.length === 0 ? (
            <EmptyState
              size="sm"
              title="Nothing needs you"
              description={actions.status_detail}
              icon={<Icon icon={CircleCheck} size="lg" />}
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {shown.map((action) => (
                <ActionRow key={action.id} action={action} />
              ))}
            </ul>
          )}

          {actions.errors.length > 0 && (
            <Typography variant="caption" tone="muted">
              Some of the board could not be read, so this list may be short: {""}
              {actions.errors.join(" · ")}
            </Typography>
          )}
        </div>
      </PanelCard>
    </section>
  );
}

function UrgencyTile({ urgency, count }: { urgency: Urgency; count: number }) {
  const style = URGENCY[urgency];
  // Zero is good news and must not be tinted like a finding — a red 0 next to
  // "Fix today" reads as an alarm at a glance, which is the opposite of true.
  const tone: StatusTone = count === 0 ? "neutral" : style.tone;
  return (
    <DrillLink
      to={BACKUP_ACTIONS_PATH}
      params={{ [URGENCY_PARAM]: urgency }}
      className="border-border hover:border-accent focus-visible:outline-accent flex items-center gap-3 rounded-lg border p-3 transition-colors focus-visible:outline-2"
    >
      <span
        aria-hidden
        className="bg-bg-muted flex size-9 shrink-0 items-center justify-center rounded-md"
      >
        <Icon icon={count === 0 ? CircleCheck : style.icon} size="sm" tone={iconTone(tone)} />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="flex items-baseline gap-2">
          <span className="text-fg text-xl font-bold tabular-nums">{formatCount(count)}</span>
          <span className="text-fg-muted text-sm font-medium">{style.label}</span>
        </span>
        {/* Clamped rather than truncated: at a narrow width the second line is
            the half that says what the bucket is for. */}
        <span className="text-fg-muted line-clamp-2 text-xs">{style.meaning}</span>
      </span>
    </DrillLink>
  );
}

export function ActionRow({ action }: { action: BackupAction }) {
  const style = URGENCY[action.urgency];
  return (
    <li className="border-border hover:border-accent rounded-lg border p-3 transition-colors">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <Icon icon={style.icon} size="sm" tone={iconTone(style.tone)} aria-hidden />
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <Typography variant="body-sm" className="font-semibold">
                {action.title}
              </Typography>
              {/* Never colour-alone: the badge carries the word too (§3.2). */}
              <Badge tone={style.tone} variant="subtle" size="xs">
                {style.label}
              </Badge>
              {action.site && (
                <Badge tone="neutral" variant="subtle" size="xs">
                  {action.site}
                </Badge>
              )}
              {action.count > 1 && (
                <Badge tone="neutral" variant="subtle" size="xs">
                  {formatCount(action.count)}
                </Badge>
              )}
            </div>
            <Typography variant="caption" tone="muted">
              {action.detail}
            </Typography>
            {/* The line that decides whether the row gets actioned or skipped. */}
            <Typography variant="caption" tone="muted" className="italic">
              {action.why}
            </Typography>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {action.since && (
            <span className="text-fg-muted text-xs whitespace-nowrap">
              {formatRelativeTime(action.since)}
            </span>
          )}
          <DrillLink
            to={action.route}
            params={action.params}
            className="text-accent text-sm font-medium whitespace-nowrap hover:underline"
          >
            Show me →
          </DrillLink>
        </div>
      </div>
    </li>
  );
}

/** Re-exported so the L2 page renders identical rows rather than a second design. */
export { ORDER, URGENCY, type Urgency };
